import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { mercadoPagoEmUso } from "./mercadoPago";

/*
 * PagBank e InfinitePay — lado do app. Aqui só há chamadas ao backend:
 * nenhum access token, refresh token, client secret ou segredo de webhook
 * passa pelo app (nem pelo APK, nem pelo localStorage).
 *
 * Onde aparecem:
 *  - DESENVOLVIMENTO (npm run start:pagamentos: fora do build de produção,
 *    emuladores ligados, REACT_APP_PAGAMENTOS_DEV=true): PagBank Sandbox e
 *    InfinitePay em teste;
 *  - PRODUÇÃO: só o PagBank, só no build do site (netlify.toml define
 *    REACT_APP_PAGBANK_AMBIENTE=producao). Desktop e Android não: a volta da
 *    autorização é para o site. A InfinitePay nunca vai para produção sem um
 *    mecanismo oficial de verificação da conta (docs/pagamentos.md).
 */

export const PAGAMENTOS_DEV = process.env.NODE_ENV !== "production"
  && process.env.REACT_APP_PAGAMENTOS_DEV === "true"
  && process.env.REACT_APP_FIREBASE_EMULADORES === "true";
const PAGBANK_PRODUCAO = process.env.NODE_ENV === "production"
  && process.env.REACT_APP_PAGBANK_AMBIENTE === "producao"
  && process.env.REACT_APP_FIREBASE_EMULADORES !== "true";

// Ambiente de cada provedor (igual ao do backend); null = não aparece
export const AMBIENTES = {
  pagbank: PAGAMENTOS_DEV ? "sandbox" : PAGBANK_PRODUCAO ? "producao" : null,
  infinitepay: PAGAMENTOS_DEV ? "teste" : null,
};
export const pagbankHabilitado = () => AMBIENTES.pagbank !== null;
export const infinitepayHabilitado = () => AMBIENTES.infinitepay !== null;
export const pagamentosExtrasHabilitados = () => pagbankHabilitado() || infinitepayHabilitado();

// Provedor recebendo o Pix dos pedidos (confirmação automática), se houver
export function provedorNosPedidos(cfg, statusMP, statusPB, statusIP) {
  if (mercadoPagoEmUso(cfg, statusMP)) return "mercadopago";
  if (pagbankHabilitado() && statusPB?.conectado) return "pagbank";
  if (infinitepayHabilitado() && statusIP?.configurada) return "infinitepay";
  return null;
}

/* "Pix QR" com a chave do vendedor em Receber pagamento: só com o PIX manual
   ativado em Configurações e nenhum provedor conectado. Com provedor, o Pix
   dele confirma o pagamento sozinho; o QR da chave não tem confirmação. */
export const pixManualNosPedidos = (cfg, statusMP, statusPB, statusIP) =>
  !!cfg?.pixEnabled && !!String(cfg?.pixChave || "").trim() && !provedorNosPedidos(cfg, statusMP, statusPB, statusIP);

// Conectar o PagBank só onde a volta da autorização cai: no desenvolvimento,
// o app local; em produção, o site (functions/.env.matriz3dsite → PAGAMENTOS_APP_URL)
export const SITE_CONEXAO_PAGBANK = "https://printcontrol.netlify.app";
export const podeConectarPagBank = () =>
  AMBIENTES.pagbank === "sandbox" || (typeof window !== "undefined" && window.location.origin === SITE_CONEXAO_PAGBANK);

export const PROVEDORES = {
  mercadopago: { nome: "Mercado Pago", doNome: "do Mercado Pago", cor: "#009ee3", metodos: [{ id: "pix", rotulo: "Pix" }] },
  pagbank: { nome: "PagBank", doNome: "do PagBank", cor: "#1a9e5c", metodos: [{ id: "pix", rotulo: "Pix" }, { id: "cartao", rotulo: "Cartão de crédito" }] },
  // O cliente escolhe Pix ou cartão na página da InfinitePay (a API não permite restringir)
  infinitepay: { nome: "InfinitePay", doNome: "da InfinitePay", cor: "#7c3aed", metodos: [{ id: "checkout", rotulo: "Pix ou cartão (o cliente escolhe)" }] },
};

// Onde um link de pagamento pode apontar (o servidor já confere; aqui é a segunda trava)
const DOMINIOS_LINK = {
  pagbank: ["pagbank.com.br", "pagseguro.uol.com.br", "pagseguro.com.br"],
  infinitepay: ["infinitepay.com.br", "infinitepay.io"],
};
const DOMINIOS_COMPROVANTE = ["infinitepay.io", "infinitepay.com.br", "cloudwalk.io"];

function urlNosDominios(url, dominios) {
  try {
    const u = new URL(String(url || ""));
    const host = u.hostname.toLowerCase();
    return u.protocol === "https:" && !u.username && !u.password && dominios.some(d => host === d || host.endsWith(`.${d}`)) ? u.toString() : null;
  } catch { return null; }
}
export const linkDePagamentoSeguro = (provedor, url) => urlNosDominios(url, DOMINIOS_LINK[provedor] || []);
export const comprovanteSeguro = url => urlNosDominios(url, DOMINIOS_COMPROVANTE);

function chamar(nome, dados) {
  return httpsCallable(getFunctions(auth.app, "us-central1"), nome)(dados).then(r => r.data);
}

// Resumo da conexão (sem nada sensível), gravado só pelo backend
export function useStatusProvedor(uid, provedor) {
  const [status, setStatus] = useState(undefined);
  useEffect(() => {
    if (!uid || !AMBIENTES[provedor]) { setStatus(null); return undefined; }
    return onSnapshot(doc(db, "users", uid, "pagamentos", `${provedor}__${AMBIENTES[provedor]}`),
      s => setStatus(s.exists() ? s.data() : null), () => setStatus(null));
  }, [uid, provedor]);
  return status;
}

// ── PagBank Connect ─────────────────────────────────────────────
/* O "vínculo" não é credencial: prova que foi ESTA aba que começou a
   conexão. Fica só na aba (sessionStorage), vale 10 minutos e é apagado
   ao ser usado. */
const CHAVE_VINCULO = "pc_pg_vinculo_pagbank";
const CHAVE_RETORNO = "pc_pg_retorno_pagbank";
const DEZ_MIN = 10 * 60 * 1000;

// opcoes.trocar: provedor atual (troca confirmada na tela); opcoes.termos: versão dos termos vistos
const opcoesConexao = (opcoes = {}) => ({
  ...(opcoes.trocar ? { trocar: opcoes.trocar } : {}), ...(opcoes.termos ? { termos: opcoes.termos } : {}),
});

export async function iniciarConexaoPagBank(opcoes = {}) {
  const { url, vinculo, expiraEm } = await chamar("pbConexaoIniciar", opcoesConexao(opcoes));
  const destino = new URL(url);
  // Login e autorização só no ambiente oficial do PagBank
  if (!["https://connect.sandbox.pagbank.com.br", "https://connect.pagbank.com.br"].includes(destino.origin)) throw new Error("Endereço de autorização inesperado.");
  try { sessionStorage.setItem(CHAVE_VINCULO, JSON.stringify({ vinculo, expiraEm: Math.min(expiraEm, Date.now() + DEZ_MIN) })); } catch {}
  window.location.assign(destino.toString());
}

/* Volta do PagBank: /#pg_conexao=pagbank&s=<status>&r=<recibo>. Lê e APAGA do
   endereço na hora e guarda na aba até a tela de Pagamentos concluir. */
export function capturarRetornoPagBank() {
  const hash = window.location.hash || "";
  if (!hash.includes("pg_conexao=pagbank")) return null;
  const params = new URLSearchParams(hash.slice(1));
  const retorno = { status: params.get("s") || "erro", recibo: params.get("r") || null };
  try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch {}
  try { sessionStorage.setItem(CHAVE_RETORNO, JSON.stringify(retorno)); } catch {}
  return retorno;
}

export function retornoPagBankPendente() {
  try { return JSON.parse(sessionStorage.getItem(CHAVE_RETORNO) || "null"); } catch { return null; }
}

export const MENSAGENS_RETORNO_PAGBANK = {
  negado: "A autorização foi cancelada no PagBank. Nada foi conectado.",
  expirado: "O tempo para autorizar acabou (10 minutos). Comece de novo.",
  usado: "Este retorno do PagBank já foi usado. Comece de novo.",
  invalido: "Retorno do PagBank inválido. Comece de novo.",
  erro: "Não foi possível concluir a conexão com o PagBank. Tente de novo.",
};

export async function concluirConexaoPagBank(retorno) {
  let salvo = null;
  try {
    salvo = JSON.parse(sessionStorage.getItem(CHAVE_VINCULO) || "null");
    sessionStorage.removeItem(CHAVE_VINCULO);
    sessionStorage.removeItem(CHAVE_RETORNO);
  } catch {}
  if (retorno?.status !== "retornou" || !retorno.recibo) throw new Error(MENSAGENS_RETORNO_PAGBANK[retorno?.status] || MENSAGENS_RETORNO_PAGBANK.erro);
  if (!salvo?.vinculo || !(salvo.expiraEm > Date.now())) throw new Error("A conexão precisa ser concluída na mesma aba em que começou, em até 10 minutos. Comece de novo.");
  return chamar("pbConexaoConcluir", { recibo: retorno.recibo, vinculo: salvo.vinculo });
}

export const desconectarPagBank = () => chamar("pbConexaoDesconectar", {});

// ── InfinitePay ──────────────────────────────────────────────────
export const definirInfiniteTag = (tag, opcoes = {}) => chamar("ipDefinirTag", { tag, ...opcoesConexao(opcoes) });
export const removerInfiniteTag = () => chamar("ipRemoverTag", {});

// ── Cobrança online de um pedido ─────────────────────────────────
// O valor NÃO vai daqui: o servidor usa o saldo do pedido.
export const criarCobrancaOnline = ({ provedor, metodo, pedidoId, requestId, parcelasMax }) =>
  chamar("pgCriarCobranca", { provedor, metodo, pedidoId, requestId, ...(parcelasMax ? { parcelasMax } : {}) });
export const consultarCobrancaOnline = cobrancaId => chamar("pgConsultarCobranca", { cobrancaId });
export const cancelarCobrancaOnline = cobrancaId => chamar("pgCancelarCobranca", { cobrancaId });
export const comprovanteCobranca = cobrancaId => chamar("pgComprovante", { cobrancaId });

// Mensagem amigável de um erro das functions (nunca detalhes internos)
export function mensagemPagamento(erro) {
  const code = erro?.code || "";
  if (code.includes("unauthenticated")) return "Sua sessão expirou. Entre de novo.";
  if (code.includes("resource-exhausted")) return "Muitas tentativas. Aguarde alguns minutos.";
  if (code.includes("unavailable") || code.includes("internal") || code.includes("deadline")) return "O provedor de pagamento não respondeu agora. Tente de novo em instantes.";
  return erro?.message || "Não foi possível concluir. Tente de novo.";
}
